import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { planService } from '../../../../../application/PlanService';
import { Driver } from '../../../../../domain/driver/Driver';
import useErrorMiddleware from '../../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { useCustomParams } from '../../../../../hooks/useCustomParams';
import { PlanDetailsActionType } from '../../context/actions';
import { usePlanDetailsContext } from '../../context/context';

type UseRetrieveActivePlanDriversResponse = ServiceResponse<Function, Array<Driver>> & {
  map: Record<string, Driver>;
};

// Hooks act as 'Adapter' layer.
export const useRetrieveActivePlanDrivers = (): UseRetrieveActivePlanDriversResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { planDetailsState, planDetailsDispatch } = usePlanDetailsContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { value: planId } = useCustomParams({ param: `planId` });

  const { isLoading, isError, isSuccess, refetch } = useQuery(
    [`planner_active_plan_drivers`, planId],
    () => planService.getPlanDrivers({ planId: planId!, project: activeProject! }),
    {
      enabled: planId !== undefined && activeProject !== undefined,
      retry: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (planDrivers) => {
        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_DRIVERS,
          payload: {
            planDrivers,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Active Plan Drivers Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: planDetailsState.drivers,
    map: planDetailsState.driversMap,
  };
};
